import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Settings, ChevronDown, HeartHandshake, Wrench, Wine, Pill, Dices, Brain, Calculator, Users, Heart, Sparkles, Smartphone, Mail, Target, Building2, Mic, Eye, CalendarDays, MessageSquareHeart } from 'lucide-react';
import { NewsletterForm } from './Newsletter';

export function Header() {
  const auth = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileDirectoryOpen, setMobileDirectoryOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileEducacionOpen, setMobileEducacionOpen] = useState(false);
  const location = useLocation();

  useEffect(function() {
    function handleScroll() {
      setScrolled(window.scrollY > 50);
    }
    window.addEventListener('scroll', handleScroll);
    return function() { window.removeEventListener('scroll', handleScroll); };
  }, []);

  function isActive(path) {
    return location.pathname === path;
  }

  var navLinks = [
    { href: '/', label: 'Inicio' },
    { href: '/zona-cero', label: 'Zona Cero' },
    { href: '/ciudades', label: 'Ciudades' },
    { href: '/blog', label: 'Blog' },
    { href: '/cotizar', label: 'Cotizar', badge: 'Gratis' }
  ];

  var educacionMenu = [
    { href: '/adicciones', label: 'Tipos de Adicciones', icon: Brain },
    { href: '/tratamientos', label: 'Tipos de Tratamiento', icon: Heart },
    { href: '/guia-tratamientos', label: 'Guía Completa', icon: Target },
    { href: '/centros-rehabilitacion-gratis', label: 'Tratamiento Gratuito', icon: Sparkles }
  ];

  var directoryMenu = [
    { href: '/centros-de-rehabilitacion-drogas-chile', label: 'Centros de Rehabilitación', icon: Building2 },
    { href: '/centros-de-rehabilitacion-de-drogas-gratis', label: 'Centros Gratuitos (SENDA)', icon: Heart },
    { href: '/profesionales', label: 'Profesionales', icon: User },
    { href: '/talleres', label: 'Talleres Terapéuticos', icon: Users },
    { href: '/grupos-aa', label: 'Grupos de Apoyo', icon: Heart }
  ];

  var toolsMenu = [
    { href: '/terapeutia', label: 'TerapeutIA · Chat IA', icon: MessageSquareHeart, badge: 'Nuevo' },
    { href: '/experiencias', label: 'Experiencias inmersivas', icon: Eye, badge: 'Nuevo' },
    { href: '/reto-21-dias', label: 'Reto 21 Días sin Drogas', icon: CalendarDays, badge: 'Nuevo' },
    { href: '/zona-cero', label: 'Charlas Zona Cero', icon: Mic },
    { href: '/herramientas/test-alcoholismo', label: 'Test Alcoholismo', icon: Wine },
    { href: '/herramientas/test-dependencia', label: 'Test Dependencia', icon: Pill },
    { href: '/herramientas/test-ludopatia', label: 'Test Ludopatía', icon: Dices },
    { href: '/herramientas/test-urica', label: 'Test URICA', icon: Brain },
    { href: '/herramientas/test-familiar', label: 'Test Riesgo Familiar', icon: Users },
    { href: '/herramientas/test-codependencia', label: 'Test Codependencia', icon: Heart },
    { href: '/calculadora', label: 'Calculadora Costos', icon: Calculator }
  ];

  function getDashboardLink() {
    if (auth.isAdmin) return '/admin';
    if (auth.isCenter) return '/center/dashboard';
    if (auth.isProfessional) return '/professional/dashboard';
    if (auth.isWorkshop) return '/workshop/dashboard';
    return '/select-role';
  }

  return (
    <header 
      className={'fixed top-0 left-0 right-0 z-50 transition-all duration-300 ' + (scrolled ? 'glass' : 'bg-transparent')} 
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-glow group-hover:shadow-glow-lg transition-shadow duration-300">
              <HeartHandshake className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-xl text-slate-50 hidden sm:block">
              SinAdicciones<span className="text-emerald-400">.org</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.slice(0, 1).map(function(link) {
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={'text-sm font-medium transition-colors duration-200 inline-flex items-center gap-1.5 ' + 
                    (isActive(link.href) 
                      ? 'text-emerald-400' 
                      : 'text-slate-400 hover:text-slate-50')}
                  data-testid={'nav-link-' + link.label.toLowerCase()}
                >
                  {link.label}
                  {link.badge && (
                    <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded uppercase tracking-wide">{link.badge}</span>
                  )}
                </Link>
              );
            })}
            {/* Directory Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={'text-sm font-medium transition-colors duration-200 flex items-center gap-1 ' + 
                    (location.pathname.startsWith('/centros') || location.pathname.startsWith('/profesionales') || location.pathname.startsWith('/talleres')
                      ? 'text-emerald-400' 
                      : 'text-slate-400 hover:text-slate-50')}
                  data-testid="nav-directory-trigger"
                >
                  Directorio
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-slate-900 border-slate-800">
                {directoryMenu.map(function(item) {
                  var ItemIcon = item.icon;
                  return (
                    <DropdownMenuItem key={item.href} asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to={item.href} className="flex items-center gap-3 text-slate-300 py-2">
                        <ItemIcon className="w-5 h-5 text-emerald-400" />
                        <span>{item.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Educación Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={'text-sm font-medium transition-colors duration-200 flex items-center gap-1 ' + 
                    (location.pathname.startsWith('/adicciones') || location.pathname.startsWith('/tratamiento')
                      ? 'text-emerald-400' 
                      : 'text-slate-400 hover:text-slate-50')}
                  data-testid="nav-educacion-trigger"
                >
                  Educación
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-slate-900 border-slate-800">
                {educacionMenu.map(function(item) {
                  var ItemIcon = item.icon;
                  return (
                    <DropdownMenuItem key={item.href} asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to={item.href} className="flex items-center gap-3 text-slate-300 py-2">
                        <ItemIcon className="w-5 h-5 text-emerald-400" />
                        <span>{item.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            {navLinks.slice(1).map(function(link) {
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={'text-sm font-medium transition-colors duration-200 inline-flex items-center gap-1.5 ' + 
                    (isActive(link.href) 
                      ? 'text-emerald-400' 
                      : 'text-slate-400 hover:text-slate-50')}
                  data-testid={'nav-link-' + link.label.toLowerCase()}
                >
                  {link.label}
                  {link.badge && (
                    <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded uppercase tracking-wide">{link.badge}</span>
                  )}
                </Link>
              );
            })}
            {/* Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={'text-sm font-medium transition-colors duration-200 flex items-center gap-1 ' + 
                    (location.pathname.startsWith('/herramientas') || location.pathname === '/calculadora'
                      ? 'text-emerald-400' 
                      : 'text-slate-400 hover:text-slate-50')}
                  data-testid="nav-tools-trigger"
                >
                  <Wrench className="w-4 h-4" />
                  Herramientas
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-slate-900 border-slate-800">
                {toolsMenu.map(function(tool) {
                  var ToolIcon = tool.icon;
                  return (
                    <DropdownMenuItem key={tool.href} asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to={tool.href} className="flex items-center gap-2 text-slate-300">
                        <ToolIcon className="w-4 h-4 text-emerald-400" />
                        <span className="flex-1">{tool.label}</span>
                        {tool.badge && (
                          <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded bg-emerald-500 text-black font-bold">{tool.badge}</span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                  <Link to="/herramientas" className="flex items-center gap-2 text-emerald-400 font-medium">
                    Ver todas las herramientas
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {auth.isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-slate-300 hover:text-slate-50 hover:bg-slate-800" 
                    data-testid="user-menu-trigger"
                  >
                    {auth.user?.picture ? (
                      <img src={(() => { var u = auth.user.picture; if (u && (u.includes('.emergent.host/api/') || u.includes('.emergentagent.com/api/'))) { try { u = new URL(u).pathname; } catch(e) {} } if (u && u.startsWith('/api/')) { var b = process.env.REACT_APP_BACKEND_URL; if (b && typeof window !== 'undefined' && !b.includes(window.location.hostname) && !b.includes('localhost')) { b = window.location.origin; } return (b || '') + u; } return u; })()} alt={auth.user.name} className="w-8 h-8 rounded-full ring-2 ring-emerald-500/30" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                    )}
                    <span className="max-w-[120px] truncate">{auth.user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-slate-50">{auth.user?.name}</p>
                    <p className="text-xs text-slate-400">{auth.user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                    <Link to="/profile" className="flex items-center gap-2 text-slate-300">
                      <User className="w-4 h-4" />Mi Perfil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                    <Link to={getDashboardLink()} className="flex items-center gap-2 text-slate-300">
                      <LayoutDashboard className="w-4 h-4" />Panel
                    </Link>
                  </DropdownMenuItem>
                  {auth.isSales && (
                    <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to="/ventas" className="flex items-center gap-2 text-emerald-400">
                        <Target className="w-4 h-4" />Panel de Ventas
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {auth.isAdmin && (
                    <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to="/admin" className="flex items-center gap-2 text-slate-300">
                        <Settings className="w-4 h-4" />Administración
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-slate-800" />
                  <DropdownMenuItem 
                    onClick={auth.logout} 
                    className="text-red-400 hover:bg-slate-800 focus:bg-slate-800 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="text-slate-400 hover:text-slate-50 hover:bg-slate-800 flex items-center gap-1" 
                      data-testid="login-button"
                    >
                      Iniciar sesión
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-800">
                    <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to="/login" className="flex items-center gap-2 text-slate-300">
                        <User className="w-4 h-4 text-emerald-400" />
                        Ingresar
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-800" />
                    <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to="/quienes-somos" className="flex items-center gap-2 text-slate-300">
                        <Users className="w-4 h-4 text-emerald-400" />
                        Quiénes Somos
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="hover:bg-slate-800 focus:bg-slate-800 cursor-pointer">
                      <Link to="/contacto" className="flex items-center gap-2 text-slate-300">
                        <Mail className="w-4 h-4 text-emerald-400" />
                        Contacto
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button 
                  asChild
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg px-6 shadow-glow hover:shadow-glow-lg transition-shadow duration-300" 
                  data-testid="header-cotizar-button"
                >
                  <Link to="/cotizar">Cotizar</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-300 hover:text-slate-50"
            onClick={function() { setMobileMenuOpen(!mobileMenuOpen); }}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900 border-t border-slate-800">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map(function(link) {
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={'block py-2 ' + (isActive(link.href) ? 'text-emerald-400 font-semibold' : 'text-slate-400')}
                  onClick={function() { setMobileMenuOpen(false); }}
                >
                  {link.label}
                </Link>
              );
            })}
            {/* Directory Section Mobile - Collapsible */}
            <div className="pt-2 border-t border-slate-800">
              <button 
                onClick={function() { setMobileDirectoryOpen(!mobileDirectoryOpen); }}
                className="flex items-center justify-between w-full py-2 text-slate-300 hover:text-white"
              >
                <span className="text-sm font-medium">Directorio</span>
                <ChevronDown className={'w-4 h-4 transition-transform ' + (mobileDirectoryOpen ? 'rotate-180' : '')} />
              </button>
              {mobileDirectoryOpen && (
                <div className="pl-2 space-y-1">
                  {directoryMenu.map(function(item) {
                    var ItemIcon = item.icon;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="flex items-center gap-2 py-2 text-slate-400 hover:text-emerald-400 text-sm"
                        onClick={function() { setMobileMenuOpen(false); }}
                      >
                        <ItemIcon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Educación Section Mobile - Collapsible */}
            <div className="pt-2 border-t border-slate-800">
              <button 
                onClick={function() { setMobileEducacionOpen(!mobileEducacionOpen); }}
                className="flex items-center justify-between w-full py-2 text-slate-300 hover:text-white"
              >
                <span className="text-sm font-medium">Educación</span>
                <ChevronDown className={'w-4 h-4 transition-transform ' + (mobileEducacionOpen ? 'rotate-180' : '')} />
              </button>
              {mobileEducacionOpen && (
                <div className="pl-2 space-y-1">
                  {educacionMenu.map(function(item) {
                    var ItemIcon = item.icon;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="flex items-center gap-2 py-2 text-slate-400 hover:text-emerald-400 text-sm"
                        onClick={function() { setMobileMenuOpen(false); }}
                      >
                        <ItemIcon className="w-4 h-4" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Tools Section Mobile - Collapsible */}
            <div className="pt-2 border-t border-slate-800">
              <button 
                onClick={function() { setMobileToolsOpen(!mobileToolsOpen); }}
                className="flex items-center justify-between w-full py-2 text-slate-300 hover:text-white"
              >
                <span className="text-sm font-medium">Herramientas</span>
                <ChevronDown className={'w-4 h-4 transition-transform ' + (mobileToolsOpen ? 'rotate-180' : '')} />
              </button>
              {mobileToolsOpen && (
                <div className="pl-2 space-y-1">
                  {toolsMenu.map(function(tool) {
                    var ToolIcon = tool.icon;
                    return (
                      <Link
                        key={tool.href}
                        to={tool.href}
                        className="flex items-center gap-2 py-2 text-slate-400 hover:text-emerald-400 text-sm"
                        onClick={function() { setMobileMenuOpen(false); }}
                      >
                        <ToolIcon className="w-4 h-4" />
                        <span className="flex-1">{tool.label}</span>
                        {tool.badge && (
                          <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded bg-emerald-500 text-black font-bold">{tool.badge}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="pt-4 border-t border-slate-800">
              {auth.isAuthenticated ? (
                <>
                  <Link
                    to={getDashboardLink()}
                    className="flex items-center gap-2 py-2 text-emerald-400"
                    onClick={function() { setMobileMenuOpen(false); }}
                  >
                    <LayoutDashboard className="w-5 h-5" />Mi Panel
                  </Link>
                  <button
                    onClick={function() { auth.logout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 py-2 text-red-400"
                  >
                    <LogOut className="w-5 h-5" />Cerrar sesión
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/quienes-somos" 
                    className="block py-2 text-slate-400 hover:text-emerald-400"
                    onClick={function() { setMobileMenuOpen(false); }}
                  >
                    Nosotros
                  </Link>
                  <Link 
                    to="/contacto" 
                    className="block py-2 text-slate-400 hover:text-emerald-400"
                    onClick={function() { setMobileMenuOpen(false); }}
                  >
                    Contacto
                  </Link>
                  <Button 
                    asChild
                    className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-lg"
                  >
                    <Link to="/login">Iniciar sesión</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-slate-900 border-t border-slate-800 py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-6">
          <div className="md:col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-glow">
                <HeartHandshake className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-50">SinAdicciones<span className="text-emerald-400">.org</span></span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              Directorio líder de centros de rehabilitación, profesionales y talleres terapéuticos en Chile. 
              Compara opciones con precios reales, duración y opiniones verificadas.
            </p>
            {/* Social Media */}
            <div className="flex items-center gap-3 mt-5">
              <a href="https://www.instagram.com/sinadicciones_org/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-pink-500/20 border border-slate-700 hover:border-pink-500/50 flex items-center justify-center transition-all duration-200" data-testid="social-instagram">
                <svg className="w-4 h-4 text-slate-400 hover:text-pink-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://web.facebook.com/profile.php?id=61585407995202" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-blue-500/20 border border-slate-700 hover:border-blue-500/50 flex items-center justify-center transition-all duration-200" data-testid="social-facebook">
                <svg className="w-4 h-4 text-slate-400 hover:text-blue-400" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@sinadicciones.org" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-500 flex items-center justify-center transition-all duration-200" data-testid="social-tiktok">
                <svg className="w-4 h-4 text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
              <a href="https://www.youtube.com/@sinadicciones_org" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-red-500/20 border border-slate-700 hover:border-red-500/50 flex items-center justify-center transition-all duration-200" data-testid="social-youtube">
                <svg className="w-4 h-4 text-slate-400 hover:text-red-400" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>

          {/* Enlaces Rápidos - Nueva sección */}
          <div>
            <h4 className="font-semibold text-slate-50 mb-4">Enlaces Rápidos</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium">
                  Buscar Centros
                </Link>
              </li>
              <li>
                <Link to="/profesionales" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium">
                  Buscar Profesionales
                </Link>
              </li>
              <li>
                <Link to="/talleres" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium">
                  Buscar Talleres
                </Link>
              </li>
              <li>
                <Link to="/herramientas" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium">
                  Tests de Autoevaluación
                </Link>
              </li>
              <li>
                <Link to="/contacto" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium">
                  Contacto
                </Link>
              </li>
              <li>
                <Link to="/quienes-somos" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium">
                  Quiénes Somos
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-50 mb-4">Directorio</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Centros
                </Link>
              </li>
              <li>
                <Link to="/profesionales" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Profesionales
                </Link>
              </li>
              <li>
                <Link to="/talleres" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Talleres
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/formacion" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Formación
                </Link>
              </li>
              <li className="pt-2 border-t border-slate-800">
                <Link to="/agregar-centro" className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors duration-200">
                  → Agregar mi Centro
                </Link>
              </li>
              <li>
                <Link to="/unirse-profesional" className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors duration-200">
                  → Unirme como Profesional
                </Link>
              </li>
              <li>
                <Link to="/registrar-taller" className="text-purple-400 hover:text-purple-300 font-medium transition-colors duration-200">
                  → Registrar mi Taller
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-slate-50 mb-4">Herramientas</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/terapeutia" className="text-teal-400 hover:text-teal-300 transition-colors duration-200 font-medium" data-testid="footer-terapeutia">
                  🤖 TerapeutIA · Chat IA
                </Link>
              </li>
              <li>
                <Link to="/zona-cero" className="text-amber-400 hover:text-amber-300 transition-colors duration-200 font-medium">
                  Charlas Zona Cero
                </Link>
              </li>
              <li>
                <Link to="/como-ayudar-a-un-familiar-con-adiccion" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium" data-testid="footer-familiar-link">
                  Ayuda para familias
                </Link>
              </li>
              <li>
                <Link to="/herramientas/test-alcoholismo" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Test Alcoholismo
                </Link>
              </li>
              <li>
                <Link to="/herramientas/test-dependencia" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Test Dependencia
                </Link>
              </li>
              <li>
                <Link to="/herramientas/test-familiar" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Test Riesgo Familiar
                </Link>
              </li>
              <li>
                <Link to="/herramientas/test-codependencia" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Test Codependencia
                </Link>
              </li>
              <li>
                <Link to="/herramientas" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Ver todos los tests
                </Link>
              </li>
              <li>
                <Link to="/calculadora" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Calculadora Costos
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-50 mb-4">Servicios</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/app" className="text-emerald-400 hover:text-emerald-300 transition-colors duration-200 font-medium flex items-center gap-1">
                  <Smartphone className="w-3 h-3" />
                  App Gratuita (Beta)
                </Link>
              </li>
              <li>
                <Link to="/tratamiento-adicciones-online" className="text-blue-400 hover:text-blue-300 transition-colors duration-200 font-medium">
                  🌱 Tratamiento Online
                </Link>
              </li>
              <li>
                <Link to="/asesoria" className="text-amber-400 hover:text-amber-300 transition-colors duration-200 font-medium">
                  Asesoría Experta
                </Link>
              </li>
              <li>
                <Link to="/formacion" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Formación Profesional
                </Link>
              </li>
              <li>
                <Link to="/ciudades" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Directorio por Ciudad
                </Link>
              </li>
              <li>
                <a href="mailto:contacto@sinadicciones.org" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  contacto@sinadicciones.org
                </a>
              </li>
              <li>
                <Link to="/contacto" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Contacto
                </Link>
              </li>
              <li>
                <Link to="/denuncias-centros-rehabilitacion-chile" className="text-red-400 hover:text-red-300 transition-colors duration-200 font-medium inline-flex items-center gap-1.5" data-testid="footer-denuncias-link">
                  <span aria-hidden="true">🚨</span> Denuncias y reclamos
                </Link>
              </li>
              <li>
                <Link to="/quienes-somos" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Quiénes Somos
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                  Política de Privacidad
                </Link>
              </li>
              <li className="text-slate-500 text-xs mt-6 pt-4 border-t border-slate-800">
                Línea de ayuda SENDA: 1412
              </li>
            </ul>
          </div>
        </div>

        {/* SEO Internal Linking Section - Directorio expandido */}
        <div className="border-t border-slate-800 mt-12 pt-10">
          {/* Portales aliados / sitios satélite */}
          <div className="bg-gradient-to-r from-blue-900/20 via-slate-900/30 to-emerald-900/20 border border-slate-800 rounded-2xl p-5 mb-8">
            <div className="flex items-start md:items-center gap-3 flex-col md:flex-row md:justify-between">
              <div>
                <p className="text-xs text-emerald-400 font-semibold uppercase tracking-wider mb-1">Portales aliados</p>
                <p className="text-sm text-slate-300">Recursos especializados de la red SinAdicciones para distintas adicciones.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://pasta-base.cl"
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 bg-slate-900 border border-red-500/30 hover:border-red-500/60 px-4 py-2 rounded-lg transition-colors group"
                  data-testid="footer-pastabase-link"
                >
                  <span className="w-2 h-2 rounded-full bg-red-500 group-hover:animate-pulse"></span>
                  <span className="text-sm font-semibold text-slate-200">pasta-base.cl</span>
                  <span className="text-xs text-slate-500 hidden sm:inline">Portal especializado</span>
                </a>
                <a
                  href="https://centroderehabilitaciondedrogas.cl"
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 bg-slate-900 border border-blue-500/30 hover:border-blue-500/60 px-4 py-2 rounded-lg transition-colors group"
                  data-testid="footer-cdrd-link"
                >
                  <span className="w-2 h-2 rounded-full bg-blue-500 group-hover:animate-pulse"></span>
                  <span className="text-sm font-semibold text-slate-200">centroderehabilitaciondedrogas.cl</span>
                  <span className="text-xs text-slate-500 hidden sm:inline">Centros especializados</span>
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-semibold text-slate-50 mb-4 text-sm">Directorio por Región</h4>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <li><Link to="/region/metropolitana" className="text-slate-400 hover:text-emerald-400 transition-colors">Metropolitana</Link></li>
                <li><Link to="/region/valparaiso" className="text-slate-400 hover:text-emerald-400 transition-colors">Valparaíso</Link></li>
                <li><Link to="/region/biobio" className="text-slate-400 hover:text-emerald-400 transition-colors">Biobío</Link></li>
                <li><Link to="/region/maule" className="text-slate-400 hover:text-emerald-400 transition-colors">Maule</Link></li>
                <li><Link to="/region/la-araucania" className="text-slate-400 hover:text-emerald-400 transition-colors">La Araucanía</Link></li>
                <li><Link to="/region/los-lagos" className="text-slate-400 hover:text-emerald-400 transition-colors">Los Lagos</Link></li>
                <li><Link to="/region/coquimbo" className="text-slate-400 hover:text-emerald-400 transition-colors">Coquimbo</Link></li>
                <li><Link to="/region/antofagasta" className="text-slate-400 hover:text-emerald-400 transition-colors">Antofagasta</Link></li>
              </ul>
              <Link to="/regiones" className="inline-block mt-3 text-emerald-400 text-xs font-medium hover:text-emerald-300" data-testid="footer-regiones-all">Ver todas las 16 regiones →</Link>
            </div>
            <div>
              <h4 className="font-semibold text-slate-50 mb-4 text-sm">Por Tipo de Adicción</h4>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <li><Link to="/adicciones/alcohol" className="text-slate-400 hover:text-emerald-400 transition-colors">Alcohol</Link></li>
                <li><Link to="/adicciones/cocaina" className="text-slate-400 hover:text-emerald-400 transition-colors">Cocaína</Link></li>
                <li><Link to="/adicciones/pasta-base" className="text-slate-400 hover:text-emerald-400 transition-colors">Pasta Base</Link></li>
                <li><Link to="/adicciones/patologia-dual" className="text-slate-400 hover:text-emerald-400 transition-colors">Patología Dual</Link></li>
                <li><Link to="/adicciones/marihuana" className="text-slate-400 hover:text-emerald-400 transition-colors">Marihuana</Link></li>
                <li><Link to="/adicciones/medicamentos" className="text-slate-400 hover:text-emerald-400 transition-colors">Benzodiacepinas</Link></li>
                <li><Link to="/adicciones/ludopatia" className="text-slate-400 hover:text-emerald-400 transition-colors">Ludopatía</Link></li>
                <li><Link to="/adicciones/conductuales" className="text-slate-400 hover:text-emerald-400 transition-colors">Conductuales</Link></li>
              </ul>
              <Link to="/adicciones" className="inline-block mt-3 text-emerald-400 text-xs font-medium hover:text-emerald-300" data-testid="footer-adicciones-all">Ver todas las adicciones →</Link>
            </div>
            <div>
              <h4 className="font-semibold text-slate-50 mb-4 text-sm">Por Público</h4>
              <ul className="space-y-2 text-xs">
                <li><Link to="/publico/adolescentes" className="text-slate-400 hover:text-emerald-400 transition-colors">Centros para Adolescentes</Link></li>
                <li><Link to="/publico/mujeres" className="text-slate-400 hover:text-emerald-400 transition-colors">Centros para Mujeres</Link></li>
                <li><Link to="/publico/adultos-mayores" className="text-slate-400 hover:text-emerald-400 transition-colors">Centros para Adultos Mayores</Link></li>
                <li className="pt-3"><Link to="/tratamiento/residencial" className="text-slate-400 hover:text-emerald-400 transition-colors">Tratamiento Residencial</Link></li>
                <li><Link to="/tratamiento/ambulatorio" className="text-slate-400 hover:text-emerald-400 transition-colors">Tratamiento Ambulatorio</Link></li>
                <li><Link to="/tratamiento/online" className="text-slate-400 hover:text-emerald-400 transition-colors">Tratamiento Online</Link></li>
                <li><Link to="/centros-de-rehabilitacion-de-drogas-gratis" className="text-slate-400 hover:text-emerald-400 transition-colors">Centros Gratuitos</Link></li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Newsletter Section */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="max-w-xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-emerald-400" />
              <h4 className="font-semibold text-slate-50">Suscríbete al Newsletter</h4>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Recibe información sobre nuevos centros, artículos y recursos gratuitos.
            </p>
            <NewsletterForm variant="inline" />
          </div>
        </div>
        
        <div className="border-t border-slate-800 mt-8 pt-8 text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} SinAdicciones.org. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
}
